import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useQuery, QueryResponseHandler } from "components/graphql";
import PaginatedTable from "./paginated-table";
import { useCurrentCustomerContext } from "components/current-customer-context";
import { useAuthDataContext } from "components/auth";
import { useTablePaginationContext } from "./table-pagination-context";
import {useDispatch} from "react-redux";

const getQuery = ({ gqlQuery, page, perPage, customerId, username, skip, debounce, variables, orderBy }) => {
  const ordering = Object.entries(orderBy).reduce((acc, [name, value]) => ({ ...acc, [name]: value }), {});

  return {
    query: gqlQuery,
    skip,
    debounce,
    variables: { offset: perPage * page, limit: perPage, customerId, username, ...ordering, ...variables },
  };
};

const ConnectedPaginatedTable = ({
  gqlQuery,
  paginatedElementsHandler,
  manuallyScopedQuery,
  debounce,
  variables,
  defaultOrdering = {},
  onEmpty,
  page,
  rowProps,
  onChangePerPage: onChangePerPageCustom,
  tableHeader,
  action,
  ...props
}) => {
  const dispatch = useDispatch();
  const [orderBy, setOrderBy] = useState(defaultOrdering);
  const { tablePaginationSize, handleSetPaginationSize } = useTablePaginationContext();
  const { currentCustomerId } = useCurrentCustomerContext();
  const { user, hasAdminPrivileges, isPartner } = useAuthDataContext();

  const onChangeOrdering = name => {
    if (Boolean(orderBy) && orderBy.hasOwnProperty(name)) {
      const lastOrdering = orderBy[name];
      const newOrdering = lastOrdering.toUpperCase() === "ASC" ? "DESC" : "ASC";
      setOrderBy({
        ...orderBy,
        [name]: newOrdering,
      });
    }
  };

  const skip =
    (!hasAdminPrivileges && !isPartner && !currentCustomerId) ||
    (Boolean(hasAdminPrivileges) && Boolean(manuallyScopedQuery) && !currentCustomerId);

  const query = useMemo(
    () =>
      getQuery({
        gqlQuery,
        page,
        perPage: tablePaginationSize,
        customerId: hasAdminPrivileges && !manuallyScopedQuery ? null : currentCustomerId,
        username: user.sub,
        skip,
        debounce,
        variables,
        orderBy,
      }),
    [
      gqlQuery,
      page,
      tablePaginationSize,
      hasAdminPrivileges,
      manuallyScopedQuery,
      currentCustomerId,
      user.sub,
      skip,
      debounce,
      variables,
      orderBy,
    ],
  );

  const { data, loading, error, refetch } = useQuery(query);
  const elements = paginatedElementsHandler(data);

  useEffect(() => {
      if (onEmpty && elements && elements.total === 0) {
        onEmpty();
      }
    }, [elements, onEmpty]);

  useEffect(() => {
      if (action && data) {
          dispatch(action(data));
      }
  },[action, data]);

  return (
    <QueryResponseHandler error={error} loading={loading} elements={elements}>
      {tableHeader}
      <PaginatedTable
        refetchData={refetch}
        paginatedElements={elements}
        page={page}
        perPage={tablePaginationSize}
        onChangePerPage={ev => {
          const elementsPerPage = parseInt(ev.target.value, 10);
          handleSetPaginationSize(elementsPerPage);
          onChangePerPageCustom && onChangePerPageCustom(elementsPerPage);
        }}
        rowProps={{ onChangeOrdering, orderBy, ...rowProps }}
        {...props}
      />
    </QueryResponseHandler>
  );
};

ConnectedPaginatedTable.propTypes = {
  /**
   * Número de página a cargar
   */
  page: PropTypes.number,
  /**
   * Clase adicional del componente.
   */
  className: PropTypes.string,
  /**
   * Query graphql que se utilizará para obtener los elementos de la lista
   **/
  gqlQuery: PropTypes.object.isRequired,
  /**
   * Función que obtiene los elementos a representar de los datos devueltos por el servidor
   **/
  paginatedElementsHandler: PropTypes.func.isRequired,
  /**
   * Componente que renderiza una fila de la tabla a partir de un elemento
   **/
  rowComponent: PropTypes.elementType.isRequired,
  /**
   * Props adicionales para el componente de fila
   **/
  rowProps: PropTypes.object,
  /**
   * Contenido de la cabecera de la tabla
   **/
  headerComponent: PropTypes.elementType,
  /**
   * Variables de la query (sin customerId, que se inyecta)
   **/
  variables: PropTypes.object,
  /**
   * Conjunto de variables de ordenación activas (par clave, valor)
   **/
  defaultOrdering: PropTypes.object,
  /**
   * Muestra o no los controles de paginación
   **/
  showPagination: PropTypes.bool,
  /**
   * Indica si la query se debouncea.
   **/
  debounce: PropTypes.bool,
  /**
   * Footer opcional de tabla
   **/
  footer: PropTypes.node,
  /**
   * Indica si la query de superadmin se ejecuta en un contexto de selección manual del customer
   **/
  manuallyScopedQuery: PropTypes.bool,
  /**
   * Callback que se llama si la tabla está vacía
   **/
  onEmpty: PropTypes.func,
  /**
   * Callback que se llama si la tabla está vacía
   **/
  onChangePage: PropTypes.func,
  /**
   * Callback que se llama cuando se cambia el número de elementos por página
   **/
  onChangePerPage: PropTypes.func,
  /**
   * Nombre del parámetro de query en la url que representa el paginado de esta tabla
   **/
  pageName: PropTypes.string,
  /**
   * Componente que se renderiza encima de la tabla
   **/
  tableHeader: PropTypes.element,
};

export default ConnectedPaginatedTable;
